import { useCallback, useEffect, useRef, useState } from 'react'
import { bindTtsSpeed, unbindTtsSpeed } from './tts-speed'
import {
  playTtsUrl,
  prepareTts,
  primeAudio,
  releaseAudio,
  streamingMediaSource,
  TtsRequestError,
} from './tts-player'
import { getTtsVoice } from './tts-voice'

export type TtsState = 'idle' | 'loading' | 'playing' | 'paused'

/** Whether the Worker can synthesize speech (`/health`, one shared fetch). */
export { ttsAvailable } from '@/lib/health-features'

/** Only one message plays at a time — starting another stops the current one. */
let stopCurrent: (() => void) | null = null

/**
 * Read-aloud playback for one message. `toggle()` asks `POST /voice/tts/prepare`
 * for the clip's URL and points an `<audio>` at it — the route streams the
 * MP3 from the provider's first chunk, so playback starts about a second
 * after the press (`web/lib/tts-player.ts`); while playing it pauses, while
 * paused it resumes, and when playback finishes the next press starts over.
 */
export type TtsErrorReason = 'failed' | 'credits_exhausted'

export function useTts(options: { projectId: string; onError?: (reason: TtsErrorReason) => void }) {
  const [state, setState] = useState<TtsState>('idle')
  const audioRef = useRef<HTMLAudioElement | null>(null)
  /** Aborts an in-flight /voice/tts/prepare on stop(). */
  const abortRef = useRef<AbortController | null>(null)
  const generationRef = useRef(0)
  const onErrorRef = useRef(options.onError)
  onErrorRef.current = options.onError

  const stop = useCallback(() => {
    generationRef.current++
    abortRef.current?.abort(new DOMException('stopped', 'AbortError'))
    abortRef.current = null
    const audio = audioRef.current
    audioRef.current = null
    if (audio) {
      unbindTtsSpeed(audio)
      // Detaching the source cancels the streaming request → the route
      // aborts the provider and caches nothing partial.
      releaseAudio(audio)
    }
    if (stopCurrent === stop) stopCurrent = null
    setState('idle')
  }, [])

  useEffect(() => stop, [stop])

  const toggle = useCallback(
    async (text: string) => {
      if (state === 'loading') {
        stop()
        return
      }
      const audio = audioRef.current
      if (audio) {
        // Pause ⇆ resume on the already-loaded audio.
        if (state === 'playing') {
          audio.pause()
          setState('paused')
        } else {
          await audio.play().catch(() => stop())
          setState('playing')
        }
        return
      }
      stopCurrent?.()
      stopCurrent = stop
      const generation = ++generationRef.current
      const controller = new AbortController()
      abortRef.current = controller
      setState('loading')
      // Created and primed synchronously in the click — see primeAudio().
      const element = new Audio()
      audioRef.current = element
      primeAudio(element)
      bindTtsSpeed(element)
      element.onended = stop
      element.onerror = stop
      let reason: TtsErrorReason = 'failed'
      try {
        const prepared = await prepareTts(
          { projectId: options.projectId, text, voice: getTtsVoice() },
          controller.signal
        )
        if (generation !== generationRef.current) return // stopped while loading
        await playTtsUrl({
          audio: element,
          url: prepared.url,
          signal: controller.signal,
          mediaSource: streamingMediaSource(),
          onPlaying: () => {
            if (generation === generationRef.current) setState('playing')
          },
        })
      } catch (err) {
        if (err instanceof TtsRequestError) reason = err.reason
        if (generation === generationRef.current) {
          stop()
          onErrorRef.current?.(reason)
        }
      }
    },
    [options.projectId, state, stop]
  )

  return { state, toggle, stop }
}
